export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export const asArray = (value) => Array.isArray(value) ? value : [];

export const responseArray = (value, key) => {
  if (Array.isArray(value)) {
    return value;
  }
  return asArray(value?.[key]);
};

// --- JWT Token Management ---

const TOKEN_KEY = 'access_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

// Helper to build headers for raw fetch() calls that need the JWT token
export const authHeaders = (extraHeaders = {}) => {
  const token = getToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  };
};

// --- Fetch with auto-attached JWT ---

const pendingGetRequests = new Map();

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const parseRetryAfter = (value) => {
  const seconds = Number(value);
  return Number.isFinite(seconds) ? seconds * 1000 : 750;
};

export const fetchJson = async (url, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const shouldDedupe = method === 'GET';
  const requestKey = shouldDedupe ? url : null;

  if (requestKey && pendingGetRequests.has(requestKey)) {
    return pendingGetRequests.get(requestKey);
  }

  const request = fetchJsonRequest(url, options, 1);

  if (requestKey) {
    pendingGetRequests.set(requestKey, request);
    request.then(
      () => pendingGetRequests.delete(requestKey),
      () => pendingGetRequests.delete(requestKey)
    );
  }

  return request;
};

const fetchJsonRequest = async (url, options = {}, retriesLeft = 0) => {
  const token = getToken();

  const response = await fetch(url, {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  });

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const body = isJson ? await response.json() : await response.text();

  if (response.status === 429 && retriesLeft > 0) {
    await wait(parseRetryAfter(response.headers.get('retry-after')));
    return fetchJsonRequest(url, options, retriesLeft - 1);
  }

  if (!response.ok) {
    const serverMessage = isJson && body?.message ? body.message : response.statusText;
    throw new ApiError(serverMessage || 'Request failed', {
      status: response.status,
      body
    });
  }

  return body;
};
